import { useContext, useEffect, useState } from 'react';
import {
  Button, Grid, TextField, MenuItem, TableContainer, Table,
  TableHead, TableBody, TableRow, TableCell, TableSortLabel,
  Box,
  useTheme,
  Snackbar,
  Alert,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { SearchSm } from '../../../../assets';
import { UserContext } from '../../../../components/shared/useUser';

import { client, OrderType, DeliveryListFilterOptions, DeliveriesListModel } from '../../../../app/services/api/orderManagementClient';
import CustomPagination from '../../../../components/forms/Pagination';
import LoadingWheel from '../../../../components/ui/LoadingWheel';
import OrderStatusChip from '../../../../components/shared/orders/OrderStatusChip';
import PageTitle from '../../../../components/shared/PageTitle';
import PageArea from '../../../../components/shared/PageArea';
import { useTranslation } from 'react-i18next';
import { OrderTypeTranslationMap, isOrderType } from '../../../../enums/OrderType';
import { applyRoundAndFormat } from '../../../../utils';
import useCurrencyCodeConfiguration from '../../../../hooks/configurations/useCurrencyCodeConfiguration';
import { CreditRequestStatus } from '../enums/DeliveryEnums';

const DeliveriesList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedStore } = useContext(UserContext);
  const { t } = useTranslation('orderHistory');
  const { hasPermissionTo } = useContext(UserContext);
  const theme = useTheme();
  
  const userIsReviewer = hasPermissionTo(['CreditRequestsReview']);

  const [orderNumberSearch, setOrderNumberSearch] = useState('');
  const [orderType, setOrderType] = useState(-1);
  const [deliveryStatus, setDeliveryStatus] = useState(-1);
  const [creditRequestStatus, setCreditRequestStatus] = useState(userIsReviewer ? CreditRequestStatus.Review : -1);
  const [deliveryDateFrom, setDeliveryDateFrom] = useState('');
  const [deliveryDateTo, setDeliveryDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<string>('');
  const [totalPages, setTotalPages] = useState(0);  
  const [orderTypes, setOrderTypes] = useState<OrderType[]>([]);
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const [orders, setOrders] = useState<DeliveriesListModel[]>([]);
  
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });
 
  useEffect(() => {
    const successMessage = (location.state as { successMessage?: string } | null)?.successMessage;
    if (successMessage) {
      setSnackbar({ open: true, message: successMessage, severity: 'success' });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);


  const getCreditRequestValue = (status: string | number | null | undefined) => {
    const statusNum = typeof status === 'string' ? parseInt(status) : status;
    switch (statusNum) {
      case CreditRequestStatus.Pending:
        return t('deliveriesList.creditRequestStatusPending');
      case CreditRequestStatus.Review:
        return t('deliveriesList.creditRequestStatusReview');
      case CreditRequestStatus.Complete:
        return t('deliveriesList.creditRequestStatusComplete');
      default:
        return t('deliveriesList.creditRequestStatusNA');
    }
  };

  useEffect(() => {
    if (!hasPermissionTo(['DeliveriesView'])) return;
    if (userIsReviewer) {
      setCreditRequestStatus(CreditRequestStatus.Review);
    }
    loadData();
    getCurrencyCodeConfiguration();
  }, [page, selectedStore]);

  const loadData = async () => {
    setLoading(true);
    await fetchOrderTypes();
    await fetchDeliveries(getFilterOptions(false));
    setLoading(false);
  };

  const fetchDeliveries = async (filters: DeliveryListFilterOptions) => {
    try {
      const filtersToUse = { ...filters };
      const ordersResponse = await client.getDeliveries(filtersToUse);

      setOrders(ordersResponse.data.items);
      setTotalPages(ordersResponse.data.totalPages);
    } catch (error) {
      console.error(t('errorLoading'), error);
    }
  };

  const fetchOrderTypes = async () => {
    try {
      const orderTypesResponse = await client.getOrderTypes(selectedStore?.storeNumber);
      setOrderTypes(orderTypesResponse.data);
    } catch (error) {
      console.error(t('errorLoading'), error);
    }
  };

  const handleSort = async (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setLoading(true);
    await fetchDeliveries({
      orderNumber: orderNumberSearch,
      orderType: orderType,
      deliveryStatus: deliveryStatus,
      creditRequestStatus: creditRequestStatus,
      deliveryDateFrom: deliveryDateFrom,
      deliveryDateTo: deliveryDateTo,
      storeNumber: selectedStore?.storeNumber,
      pageNumber: page,
      pageSize: pageSize,
      sort: property,
      direction: isAsc ? 'desc' : 'asc',
    });
    setLoading(false);
  };

  const handleSearch = async () => {
    setLoading(true);
    await fetchDeliveries(getFilterOptions(true));
    setLoading(false);
  };

  const handleClearFilters = async () => {
    setLoading(true);
    const defaultCreditRequestStatus = userIsReviewer ? CreditRequestStatus.Review : -1;
    await fetchDeliveries({
      orderNumber: '',
      orderType: -1,
      creditRequestStatus: defaultCreditRequestStatus,
      deliveryStatus: -1,
      deliveryDateFrom: '',
      deliveryDateTo: '',
      storeNumber: selectedStore?.storeNumber,
      pageNumber: 0,
      pageSize: 10,
      sort: 'OrderNumber',
      direction: 'desc',
    });
    setPage(0);
    setPageSize(10);
    setOrderNumberSearch('');
    setOrderType(-1);
    setDeliveryStatus(-1);
    setCreditRequestStatus(defaultCreditRequestStatus);
    setDeliveryDateFrom('');
    setDeliveryDateTo('');
    setLoading(false);
  };

  const getFilterOptions = (resetPage: boolean) => {
    const filterOptions: DeliveryListFilterOptions = {
      storeNumber: selectedStore?.storeNumber,
      orderNumber: orderNumberSearch,
      orderType: orderType,
      deliveryStatus: deliveryStatus,
      creditRequestStatus: creditRequestStatus,
      deliveryDateFrom: deliveryDateFrom,
      deliveryDateTo: deliveryDateTo,
      pageNumber: resetPage ? 0 : page,
      pageSize: pageSize,
      sort: orderBy,
      direction: order,
    };
    return filterOptions;
  };

  return (
    <PageArea>
      <Box>
        <Grid item
          xs={12}
          sx={{ textAlign: 'left' }}>
          <PageTitle subTitle={t('deliveriesList.deliveriesSubtitle')}>{t('deliveries')}</PageTitle>

        </Grid>
        <Box mt={5}>
          <Grid item
            container
            spacing={6}
            p={3}>
            <Grid item
              lg={2.4}
              xs={12}>
              <TextField
                fullWidth
                size='small'
                label={t('searchbyOrderNumber')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleSearch();
                  }
                }}
                value={orderNumberSearch}
                onChange={(e) => setOrderNumberSearch(e.target.value)}
              />
            </Grid>
            <Grid item
              lg={2.4}
              xs={12}>
              <TextField
                fullWidth
                size='small'
                label={t('orderType')}
                value={orderType}
                required
                onChange={(e) => setOrderType(Number(e.target.value))}
                select
              >
                <MenuItem value="-1">{t('ordersType.all')}</MenuItem>
                {orderTypes.map((type) => (
                  <MenuItem key={type.id}
                    value={type.id}>
                    {isOrderType(type.id.toString()) ? t(OrderTypeTranslationMap[type.name as keyof typeof OrderTypeTranslationMap]) : type.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              lg={2.4}
              xs={12}>
              <TextField
                fullWidth
                size='small'
                label={t('deliveryDateFrom', 'Delivery Date From')}
                type="date"
                InputLabelProps={{ shrink: true }}
                value={deliveryDateFrom}
                onChange={(e) => setDeliveryDateFrom(e.target.value)} />
            </Grid>
            <Grid item
              lg={2.4}
              xs={12}>
              <TextField
                fullWidth
                size='small'
                label={t('deliveryDateTo', 'Delivery Date To')}
                type="date"
                InputLabelProps={{ shrink: true }}
                value={deliveryDateTo}
                onChange={(e) => setDeliveryDateTo(e.target.value)} />
            </Grid>
            <Grid item
              lg={2.4}
              xs={12}>
              <TextField
                fullWidth
                size='small'
                label={t('deliveriesList.deliveryStatus')}
                value={deliveryStatus}
                required
                onChange={(e) => setDeliveryStatus(Number(e.target.value))}
                select
              >
                <MenuItem value="-1">{t('all')}</MenuItem>
                <MenuItem value="0">{t('deliveriesList.deliveryStatusReceived')}</MenuItem>
                <MenuItem value="1">{t('deliveriesList.deliveryStatusNotReceived')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item
              lg={2.4}
              xs={12}>
              <TextField
                fullWidth
                size='small'
                label={t('deliveriesList.creditRequestStatus')}
                value={creditRequestStatus}
                required
                onChange={(e) => setCreditRequestStatus(Number(e.target.value))}
                select
              >
                <MenuItem value="-1">{t('all')}</MenuItem>
                <MenuItem value="1">{t('deliveriesList.creditRequestStatusReview')}</MenuItem>
                <MenuItem value="2">{t('deliveriesList.creditRequestStatusComplete')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item
              lg={2.4}
              xs={12}>
              <Button
                fullWidth
                variant="secondary"
                size="lg"
                onClick={handleClearFilters}
              >
                {t('clearFilters')}
              </Button>
            </Grid>
            <Grid item
              lg={2.4}
              xs={12}>
              <Button
                fullWidth
                variant="primary"
                size="lg"
                startIcon={<SearchSm />}
                onClick={handleSearch}
              >
                {t('search')}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
      {loading ? <LoadingWheel /> :
        <TableContainer sx={{ borderRadius: '12px' }}
          component={'div'}>
          <Table
            size="small"
            aria-label="a table"
          >
            <TableHead
              sx={{
                paddingTop: '10px',
                backgroundColor: theme.palette.custom.gray[200],
              }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}
                  align="left">
                  <TableSortLabel
                    active={orderBy === 'OrderNumber'}
                    direction={order}
                    onClick={() => handleSort('OrderNumber')}>
                    {t('orderNumber')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}
                  align="left">
                  <TableSortLabel
                    active={orderBy === 'OrderType'}
                    direction={order}
                    onClick={() => handleSort('OrderType')}>
                    {t('orderType')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}
                  align="center">
                  <TableSortLabel
                    active={orderBy === 'ShippedQuantity'}
                    direction={order}
                    onClick={() => handleSort('ShippedQuantity')}>
                    {t('deliveriesList.shippedQuantity')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}
                  align="center">
                  <TableSortLabel
                    active={orderBy === 'ValueShipped'}
                    direction={order}
                    onClick={() => handleSort('ValueShipped')}>
                    {t('deliveriesList.valueShipped')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}
                  align="center">
                  <TableSortLabel
                    active={orderBy === 'DeliveryDate'}
                    direction={order}
                    onClick={() => handleSort('DeliveryDate')}>
                    {t('deliveryDate')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}
                  align="center">
                  <TableSortLabel
                    active={orderBy === 'ReceivedDate'}
                    direction={order}
                    onClick={() => handleSort('ReceivedDate')}>
                    {t('deliveriesList.receivedDate')}
                  </TableSortLabel>
                </TableCell>
                {!userIsReviewer &&
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'DeliveryStatus'}
                    direction={order}
                    onClick={() => handleSort('DeliveryStatus')}>
                    {t('deliveriesList.deliveryStatus')}
                  </TableSortLabel>
                </TableCell>
                }
                {userIsReviewer &&
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    <TableSortLabel
                      active={orderBy === 'CreditRequestAmount'}
                      direction={order}
                      onClick={() => handleSort('CreditRequestAmount')}>
                      {t('deliveriesList.creditRequestAmount')}
                    </TableSortLabel>
                  </TableCell>
                }
                <TableCell sx={{ fontWeight: 'bold' }}
                  align="center">
                  <TableSortLabel
                    active={orderBy === 'CreditRequestStatus'}
                    direction={order}
                    onClick={() => handleSort('CreditRequestStatus')}>
                    {t('deliveriesList.creditRequestStatus')}
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow
                  key={order.OrderId}
                  hover
                  onClick={() => navigate(`/deliveries/${order.OrderId}`)}
                >
                  <TableCell>
                    <Grid container
                      alignItems="center">
                      <Grid item>
                        {order.OrderId}
                      </Grid>
                    </Grid>
                  </TableCell>
                  <TableCell>{order.OrderType}</TableCell>
                  <TableCell align="center">{order.ShippedQuantity}</TableCell>
                  <TableCell align="center">{applyRoundAndFormat(parseInt(order.ValueShipped ?? 0), currencyCode)}</TableCell>
                  <TableCell align="center">{order.DeliveryDate}</TableCell>
                  <TableCell align="center">{order.DeliveryReceivedDate?.length > 0 ? order.DeliveryReceivedDate : 'N/A'}</TableCell>
                  {!userIsReviewer && <TableCell align="center">{t(`deliveriesList.${order.DeliveryStatus}`)}</TableCell>}
                  {userIsReviewer &&
                    <TableCell align="center">
                      {order.ClaimValue ? applyRoundAndFormat((order.ClaimValue / 100), currencyCode) : t('deliveriesList.creditRequestStatusNA')}
                    </TableCell>
                  }
                  <TableCell align="center">{order.CreditRequestStatus ? <OrderStatusChip orderStatus={getCreditRequestValue(order.CreditRequestStatus)} /> : t('deliveriesList.creditRequestStatusNA')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <CustomPagination
            page={page}
            setPage={setPage}
            maxPages={totalPages}
            breakpointForChangeDisplay={120000}
          />
        </TableContainer>
      }      
      <Snackbar open={snackbar.open}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default DeliveriesList;
